import { Download, Check } from 'lucide-react';
import type { NostrEvent } from '@nostrify/nostrify';
import { Button } from '@/components/ui/button';
import { useAuthor } from '@/hooks/useAuthor';
import { useOfflineLibrary } from '@/hooks/useOfflineLibrary';
import { useToast } from '@/hooks/useToast';
import { OfflineQuotaError } from '@/lib/offlineLibrary';
import { articleTitle } from '@/lib/nip54';
import { cn } from '@/lib/utils';

interface OfflineSaveButtonProps {
  event: NostrEvent;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * Saves a wiki article into the browser's offline library (see
 * `useOfflineLibrary`) so it stays readable with no relay connection —
 * useful on a plane, on a mostly-offline e-ink e-reader, or on a flaky
 * connection. Styled to match the other article action buttons
 * (Star/Zap/Comments/Highlight/Reader).
 *
 * Unlike Star, this is purely local: it publishes nothing to Nostr and
 * needs no login, so it's always available. The author's name/picture
 * are captured alongside the event because profile metadata (kind 0)
 * would otherwise need a relay to resolve, and a saved article should
 * never render with a bare pubkey once offline.
 */
export function OfflineSaveButton({ event, size = 'sm', className }: OfflineSaveButtonProps) {
  const { isSaved, save, remove } = useOfflineLibrary();
  const { toast } = useToast();
  const author = useAuthor(event.pubkey);

  const saved = isSaved(event);

  const handleClick = () => {
    if (saved) {
      remove(event);
      toast({
        title: 'Removed from offline library',
        description: `"${articleTitle(event)}" is no longer saved on this device.`,
      });
      return;
    }

    try {
      save(event, {
        name: author.data?.metadata?.name,
        picture: author.data?.metadata?.picture,
      });
      toast({
        title: 'Saved for offline reading',
        description: `"${articleTitle(event)}" is now available on this device without a connection.`,
      });
    } catch (error) {
      toast({
        title: "Couldn't save this article",
        description:
          error instanceof OfflineQuotaError
            ? 'This browser is out of storage space. Remove a few saved articles and try again.'
            : 'Something went wrong writing to this browser\u2019s storage.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Button
      type="button"
      variant={saved ? 'default' : 'outline'}
      size={size}
      onClick={handleClick}
      title={saved ? 'Remove from offline library' : 'Save for offline reading'}
      className={cn('gap-1.5', className)}
    >
      {saved ? <Check className="size-4" /> : <Download className="size-4" />}
      <span className="hidden sm:inline">{saved ? 'Saved' : 'Save'}</span>
    </Button>
  );
}
